import { createContext, useContext, useEffect, useMemo, type JSX, type ReactNode } from 'react';
import type { HotkeyAction } from '../../engine/api';
import { useActions, useRuntime, useStudio, useUiContext } from '../context';
import { attempt } from '../safe';
import { createBuiltinActions, type BuiltinAction } from './builtins';
import { findAction, formatChord, isTypingTarget, resolveBindings, type BindableAction } from './chords';

export interface HotkeyRegistry {
  builtins: BuiltinAction[];
  /** Built-in actions followed by runtime (plugin) contributions. */
  all: (HotkeyAction & { defaults?: readonly string[]; group?: string })[];
}

const HotkeyContext = createContext<HotkeyRegistry | null>(null);

export function useHotkeyRegistry(): HotkeyRegistry {
  const value = useContext(HotkeyContext);
  if (!value) throw new Error('Hotkey registry is missing');
  return value;
}

export const isMac = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');

/** Display text of the first chord bound to an action, for menu shortcut columns. */
export function shortcutLabel(
  registry: HotkeyRegistry,
  stored: Readonly<Record<string, readonly string[]>>,
  actionId: string,
): string | undefined {
  const action = registry.all.find((a) => a.id === actionId);
  if (!action) return undefined;
  const chords = resolveBindings([action as BindableAction], stored)[actionId] ?? [];
  const preferred = chords.find((c) => c.includes('Meta') === isMac()) ?? chords[0];
  return preferred ? formatChord(preferred, isMac()) : undefined;
}

/** Listens for bound chords on the window and exposes the action list to menus and settings. */
export function HotkeyManager({ children }: { children: ReactNode }): JSX.Element {
  const studio = useStudio();
  const actions = useActions();
  const { ui } = useUiContext();
  const contributed = useRuntime((r) => r.hotkeyActions);

  const registry = useMemo<HotkeyRegistry>(() => {
    const builtins = createBuiltinActions(studio, actions);
    const taken = new Set(builtins.map((a) => a.id));
    return { builtins, all: [...builtins, ...contributed.filter((a) => !taken.has(a.id))] };
  }, [studio, actions, contributed]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // Only nudging may auto-repeat; a held key must not fire undo or delete repeatedly.
      if (event.defaultPrevented || (event.repeat && !event.code.startsWith('Arrow'))) return;
      if (isTypingTarget(event.target)) return;
      const state = ui.getState();
      if (state.dialogs.length > 0 || state.menu) return;
      // Undo or Delete in the middle of a drag would pull the document out from under it.
      if (state.gesture) return;
      if (event.target instanceof Element && event.target.closest('[role="menu"],[role="dialog"],[role="slider"]')) return;
      const bindings = resolveBindings(registry.all as BindableAction[], studio.project.getState().project.hotkeys);
      const id = findAction(event, bindings, registry.all.map((a) => a.id));
      if (!id) return;
      const action = registry.all.find((a) => a.id === id);
      if (!action) return;
      // Leave the browser's own copy alone while the user has text selected (an error message, a link).
      if (id === 'edit.copy' && (window.getSelection()?.toString() ?? '') !== '') return;
      event.preventDefault();
      attempt(studio, () => action.run());
      if (id.startsWith('nudge.')) return;
      studio.project.sealHistory();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [studio, ui, registry]);

  return <HotkeyContext.Provider value={registry}>{children}</HotkeyContext.Provider>;
}
